import React, { useEffect } from 'react';
import inputSanitizationService from '../../services/inputSanitizationService';

/**
 * GlobalInputProtection - Provides application-wide input protection
 * This component should be wrapped around the entire app to protect all inputs
 */
const GlobalInputProtection = ({ children }) => {
  useEffect(() => {
    // Attach global event listeners for input protection
    const handleGlobalKeyDown = (e) => {
      const target = e.target;
      
      // Only process input and textarea elements, but skip file inputs
      if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') || target.type === 'file') {
        return;
      }

      // Skip if already processed by sanitized components
      if (target.dataset.sanitized === 'true') {
        return;
      }

      // Allow all modifier keys and special keys
      const allowedKeys = [
        'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab', 'Enter', 'Escape',
        'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Home', 'End', 'PageUp', 'PageDown', 'Insert', 'F1', 'F2', 'F3', 'F4',
        'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'
      ];

      // Allow modifier keys and special keys
      if (allowedKeys.includes(e.key) || e.ctrlKey || e.altKey || e.metaKey) {
        return;
      }

      // Only validate actual character input, not key combinations
      if (e.key.length === 1) {
        // Check for dangerous characters only in actual typed content
        if (!inputSanitizationService.isSafeInput(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          // Silently block dangerous characters without showing error message
        }
      }
    };

    const handleGlobalPaste = (e) => {
      const target = e.target;
      
      // Only process input and textarea elements, but skip file inputs
      if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') || target.type === 'file') {
        return;
      }

      // Skip if already processed by sanitized components
      if (target.dataset.sanitized === 'true') {
        return;
      }

      const pastedText = e.clipboardData.getData('text');
      const validation = inputSanitizationService.sanitizeInput(pastedText);

      if (!validation.isValid) {
        e.preventDefault();
        e.stopPropagation();
        // Silently block invalid content without showing error message
        
        // Insert sanitized version if available
        if (validation.sanitized && validation.sanitized !== pastedText) {
          target.value = target.value + validation.sanitized;
        }
      }
    };

    const handleGlobalInput = (e) => {
      const target = e.target;
      
      // Only process input and textarea elements, but skip file inputs
      if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') || target.type === 'file') {
        return;
      }

      // Skip if already processed by sanitized components
      if (target.dataset.sanitized === 'true') {
        return;
      }

      const inputType = target.type === 'email' ? 'email' : 
                       target.type === 'tel' ? 'phone' : 
                       target.tagName === 'TEXTAREA' ? 'textarea' : 'text';

      const sanitized = inputSanitizationService.realTimeSanitize(target.value, inputType);

      if (sanitized !== target.value) {
        target.value = sanitized;
        // Silently sanitize input without showing error message
      }
    };

    const handleGlobalFocus = (e) => {
      const target = e.target;
      
      // Only process input and textarea elements, but skip file inputs
      if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') || target.type === 'file') {
        return;
      }

      // Add protection attributes if not already present
      if (!target.dataset.protectionEnabled) {
        target.dataset.protectionEnabled = 'true';
        
        // Add specific protection based on input type
        if (target.type === 'email') {
          target.dataset.inputType = 'email';
        } else if (target.type === 'tel' || target.type === 'phone') {
          target.dataset.inputType = 'phone';
        } else if (target.type === 'number') {
          target.dataset.inputType = 'number';
        } else if (target.tagName === 'TEXTAREA') {
          target.dataset.inputType = 'textarea';
        }
      }
    };

    // Attach event listeners
    document.addEventListener('keydown', handleGlobalKeyDown, true);
    document.addEventListener('paste', handleGlobalPaste, true);
    document.addEventListener('input', handleGlobalInput, true);
    document.addEventListener('focus', handleGlobalFocus, true);

    // Scan existing inputs on mount
    const scanExistingInputs = () => {
      const inputs = document.querySelectorAll('input:not([data-sanitized="true"]):not([type="file"]), textarea:not([data-sanitized="true"])');
      inputs.forEach(input => {
        if (!input.dataset.protectionEnabled) {
          input.dataset.protectionEnabled = 'true';
        }
      });
    };

    // Initial scan
    scanExistingInputs();

    // Rescan on DOM changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const newInputs = node.querySelectorAll ? 
                node.querySelectorAll('input:not([data-sanitized="true"]):not([type="file"]), textarea:not([data-sanitized="true"])') : 
                [];
              newInputs.forEach(input => {
                if (!input.dataset.protectionEnabled) {
                  input.dataset.protectionEnabled = 'true';
                }
              });
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
      document.removeEventListener('paste', handleGlobalPaste, true);
      document.removeEventListener('input', handleGlobalInput, true);
      document.removeEventListener('focus', handleGlobalFocus, true);
      observer.disconnect();
    };
  }, []);

  return <>{children}</>;
};

export default GlobalInputProtection;